import type { JobDescription } from '../types'

/** The column contract of FR-12. Export and import use this same list. */
/**
 * Whether this file is a spreadsheet of job descriptions at all.
 *
 * The columns are positional, so a row has all of them: a value only means
 * something because of which comma it sits between. A line with one field is
 * not a short row, it is not a row.
 *
 * Kept in step with the real checker deliberately. A `.csv` holding Java source
 * was accepted with no complaint, and the stand-in accepted it too, so the
 * browser without a backend showed the same nonsense.
 */
export function holdsBulkRows(lines: string[]): boolean {
  return lines.some((l) => splitCsvLine(l).length >= BULK_COLUMNS.length)
}

export const BULK_COLUMNS = [
  'title',
  'family',
  'level',
  'location',
  'payMin',
  'payMax',
  'summary',
  'responsibilities',
  'skills',
  'qualifications',
  'status',
] as const

const escape = (v: unknown) => {
  const t = v == null ? '' : String(v)
  return /[",\r\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t
}

/** Splits one CSV line, honouring double-quoted fields containing commas. */
export function splitCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let quoted = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') {
        cur += '"'
        i++
      } else if (c === '"') {
        quoted = false
      } else {
        cur += c
      }
    } else if (c === '"') {
      quoted = true
    } else if (c === ',') {
      out.push(cur)
      cur = ''
    } else {
      cur += c
    }
  }
  out.push(cur)
  return out.map((x) => x.trim())
}

/**
 * Produces a file that uploads straight back in (FR-4 and FR-12 agree on the
 * columns), so a team can edit their library in a spreadsheet and re-import.
 */
export function jdsToCsv(list: JobDescription[]): string {
  const rows = list.map((j) => [
    j.title,
    j.family,
    j.level,
    j.location,
    j.payMin,
    j.payMax,
    j.summary,
    j.responsibilities.join(' | '),
    j.skills.join(' | '),
    j.quals.join(' | '),
    j.status,
  ])
  return [BULK_COLUMNS as readonly unknown[], ...rows]
    .map((r) => r.map(escape).join(','))
    .join('\r\n')
}

export function jdToDocument(j: JobDescription, history: { version: number; date: string; note: string; by: string }[]): string {
  const L: string[] = []
  L.push(j.title)
  L.push('='.repeat(Math.max(4, j.title.length)))
  L.push('')
  L.push(`${j.family} · ${j.level} · ${j.location}`)
  L.push(`Salary range: $${Math.round(j.payMin * 1000).toLocaleString('en-US')} – $${Math.round(j.payMax * 1000).toLocaleString('en-US')}`)
  L.push(`Version v${j.version} · ${j.status} · last updated ${j.updated}`)
  L.push('')
  L.push('SUMMARY')
  L.push(j.summary || '')
  const section = (title: string, arr: string[]) => {
    if (!arr.length) return
    L.push('')
    L.push(title)
    arr.forEach((x) => L.push('- ' + x))
  }
  section('RESPONSIBILITIES', j.responsibilities)
  section('REQUIRED SKILLS', j.skills)
  section('QUALIFICATIONS', j.quals)
  if (history.length) {
    L.push('')
    L.push('VERSION HISTORY')
    history.forEach((h) => L.push(`v${h.version}  ${h.date}  ${h.note}  (${h.by})`))
  }
  return L.join('\n')
}

/**
 * The file behind "Use a sample file".
 *
 * Every row imports. This is the product showing somebody how a spreadsheet is
 * meant to look, so a row that fails is the wrong lesson: it reads as the sample
 * being broken rather than as a demonstration.
 *
 * Two rows used to be deliberately wrong. One carried the job family "Robotix",
 * a misspelling of "Robotics", to show the message that names a bad value. The
 * other repeated a title to show a duplicate being refused, which is no longer
 * refused at all. Both are gone. The error messages are worth having and are
 * covered by the tests; they do not belong in the file handed to a new customer.
 */
export const SAMPLE_BULK_CSV = [
  'title,family,level,location,payMin,payMax,summary,responsibilities,skills,qualifications,status',
  'Staff Perception Engineer,Perception,Staff,Detroit MI,195,235,"Owns radar and lidar fusion for the Detroit fleet.","Own the fusion stack|Set perception KPIs","C++17|TensorRT|Sensor fusion","Master’s or PhD preferred|9+ years in perception",Published',
  'Data Platform Engineer,Data Engineering,Senior,Austin TX,170,200,"Runs the warehouse and streaming platform.","Operate the warehouse|Own data contracts","Python|Spark|dbt","6+ years in data engineering",Published',
  'Junior Simulation Engineer,Software Engineering,Junior,Austin TX,100,120,"Builds scenarios for the regression suite.","Write scenarios|Triage sim failures","Python|Docker","Bachelor’s in CS or related",Draft',
  'Motion Planning Lead,Robotics,Senior,Austin TX,180,210,"Leads the motion planning team for urban driving.","Own the planning roadmap|Set trajectory quality targets","C++17|ROS 2|Trajectory optimisation","8+ years in robotics, 2 leading a team",Published',
  'Machine Learning Engineer,Machine Learning,Mid,Austin TX,155,185,"Trains and ships the behaviour prediction models.","Train prediction models|Own model evaluation","Python|PyTorch|Feature engineering","4+ years shipping models to production",Published',
].join('\n')

/**
 * Builds a large valid file so the preview can be checked at the size the
 * definition of done names. Every title is distinct, so nothing is flagged and
 * the whole set is scrollable.
 */
export function bigSampleCsv(rows = 5000): string {
  const families = [
    'Data Engineering',
    'Machine Learning',
    'Robotics',
    'Perception',
    'Software Engineering',
  ]
  const levels = ['Junior', 'Mid', 'Senior', 'Staff', 'Principal']
  const cities = ['Austin TX', 'Seattle WA', 'Detroit MI', 'Boston MA', 'Pittsburgh PA']
  const out: string[] = [BULK_COLUMNS.join(',')]
  for (let i = 0; i < rows; i++) {
    const family = families[i % families.length]
    const level = levels[i % levels.length]
    const min = 100 + (i % 180)
    out.push(
      [
        `${level} ${family} Engineer ${i + 1}`,
        family,
        level,
        cities[i % cities.length],
        String(min),
        String(min + 30),
        `"Owns a ${family.toLowerCase()} component for the team."`,
        '"Ship the component|Hold the quality bar"',
        '"Python|C++17"',
        `"${level} level experience"`,
        i % 7 === 0 ? 'Draft' : 'Published',
      ].join(','),
    )
  }
  return out.join('\n')
}
